import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Loader2, Search, Filter, Eye, Trash2, CheckCircle, 
  XCircle, Clock, Phone, Mail, Calendar, Users, DollarSign, ArrowRight, Plus, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const initialNewQuote = {
    name: '', email: '', phone: '', venue: '',
    event_date: '', event_time: '', guest_count: '',
    event_type: '', message: '', source: 'manual',
    line_items: [],            // [{description, quantity, unit_price}]
    tax_rate: 0.13,            // HST 13%
    include_gratuity: false,
    gratuity_rate: 0.18,
  };
  const [newQuote, setNewQuote] = useState(initialNewQuote);

  // Compute totals from line_items + tax + gratuity
  const computeQuoteTotals = (q) => {
    const subtotal = (q.line_items || []).reduce((s, li) => {
      const qty = parseFloat(li.quantity) || 0;
      const up = parseFloat(li.unit_price) || 0;
      return s + qty * up;
    }, 0);
    const tax = subtotal * (parseFloat(q.tax_rate) || 0);
    const gratuity = q.include_gratuity ? subtotal * (parseFloat(q.gratuity_rate) || 0) : 0;
    const total = subtotal + tax + gratuity;
    return { subtotal, taxAmount: tax, gratuityAmount: gratuity, total, menuSubtotal: subtotal, platedSubtotal: 0, barSubtotal: 0, addonsSubtotal: 0, rentalsSubtotal: 0 };
  };
  const updateLineItem = (idx, patch) => {
    setNewQuote(q => ({ ...q, line_items: q.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  };
  const addLineItem = () => {
    setNewQuote(q => ({ ...q, line_items: [...(q.line_items || []), { description: '', quantity: '', unit_price: '' }] }));
  };
  const removeLineItem = (idx) => {
    setNewQuote(q => ({ ...q, line_items: q.line_items.filter((_, i) => i !== idx) }));
  };

  const createQuoteRequest = async ({ convertToContract: alsoConvert = false } = {}) => {
    if (!newQuote.name?.trim() || !newQuote.email?.trim()) {
      toast({ title: 'Name and email are required', variant: 'destructive' });
      return;
    }
    try {
      setCreating(true);
      const totals = computeQuoteTotals(newQuote);
      // Sanitize line items (drop blank rows, coerce numbers)
      const lineItems = (newQuote.line_items || [])
        .filter(li => (li.description || '').trim() || li.quantity || li.unit_price)
        .map(li => ({
          description: (li.description || '').trim(),
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
          line_total: (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
        }));
      const payload = {
        ...newQuote,
        guest_count: newQuote.guest_count ? parseInt(newQuote.guest_count) : null,
        event_type: newQuote.event_type || null,
        line_items: lineItems,
        totals,
      };
      const created = await axios.post(`${API}/api/quote-requests`, payload);
      const newQuoteId = created.data?.id;

      if (alsoConvert && newQuoteId) {
        const convertResp = await axios.post(`${API}/api/quote-requests/${newQuoteId}/convert-to-contract`);
        toast({
          title: 'Quote saved & contract started',
          description: `Contract ${convertResp.data?.contract_number} created as draft.`
        });
        setShowCreateModal(false);
        setNewQuote(initialNewQuote);
        // Brief delay so toast can mount before route change unmounts it
        setTimeout(() => navigate('/contracts'), 600);
        return;
      }

      toast({ title: 'Quote request created' });
      setShowCreateModal(false);
      setNewQuote(initialNewQuote);
      fetchQuotes();
    } catch (error) {
      toast({ title: 'Error creating quote request', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/quote-requests`);
      setQuotes(response.data);
    } catch (error) {
      toast({ title: 'Error loading quotes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const filterQuotes = useCallback(() => {
    let filtered = [...quotes];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        q.name?.toLowerCase().includes(term) ||
        q.email?.toLowerCase().includes(term) ||
        q.quote_number?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    
    setFilteredQuotes(filtered);
  }, [quotes, searchTerm, statusFilter]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  useEffect(() => {
    filterQuotes();
  }, [filterQuotes]);

  const updateStatus = async (quoteId, status) => {
    try {
      await axios.patch(`${API}/api/quote-requests/${quoteId}/status?status=${status}`);
      toast({ title: 'Status updated' });
      fetchQuotes();
    } catch (error) {
      toast({ title: 'Error updating status', variant: 'destructive' });
    }
  };

  const convertToContract = async (quoteId) => {
    try {
      setConverting(true);
      const response = await axios.post(`${API}/api/quote-requests/${quoteId}/convert-to-contract`);
      toast({ 
        title: 'Converted to Contract!', 
        description: `Contract ${response.data.contract_number} created.` 
      });
      setShowDetailModal(false);
      fetchQuotes();
      // Navigate to contracts page
      navigate('/contracts');
    } catch (error) {
      toast({ title: 'Error converting to contract', variant: 'destructive' });
    } finally {
      setConverting(false);
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote request?')) return;
    
    try {
      await axios.delete(`${API}/api/quote-requests/${quoteId}`);
      toast({ title: 'Quote deleted' });
      fetchQuotes();
    } catch (error) {
      toast({ title: 'Error deleting quote', variant: 'destructive' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'contacted': return 'bg-yellow-500';
      case 'converted': return 'bg-green-500';
      case 'declined': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock size={14} />;
      case 'contacted': return <Phone size={14} />;
      case 'converted': return <CheckCircle size={14} />;
      case 'declined': return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="quote-management-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Quote Requests</h1>
          <p className="text-gray-600">Manage incoming quote requests from potential clients</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {filteredQuotes.length} of {quotes.length} quotes
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
            data-testid="new-quote-request-btn"
          >
            <Plus size={18} className="mr-1" />New Quote Request
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'New', status: 'new', color: 'bg-blue-500' },
          { label: 'Contacted', status: 'contacted', color: 'bg-yellow-500' },
          { label: 'Converted', status: 'converted', color: 'bg-green-500' },
          { label: 'Declined', status: 'declined', color: 'bg-red-500' },
        ].map(stat => (
          <Card key={stat.status} className="border-none shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-[#1e3a5f]">
                    {quotes.filter(q => q.status === stat.status).length}
                  </p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
                <div className={`w-3 h-3 rounded-full ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-none shadow-md mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search by name, email, or quote number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="quote-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quote List */}
      {filteredQuotes.length === 0 ? (
        <Card className="border-none shadow-md">
          <CardContent className="p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-500">No quote requests found</h3>
            <p className="text-gray-400">Quote requests from the public calculator will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredQuotes.map(quote => (
            <Card key={quote.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg text-[#1e3a5f]">{quote.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs text-white flex items-center gap-1 ${getStatusColor(quote.status)}`}>
                        {getStatusIcon(quote.status)}
                        {quote.status}
                      </span>
                      <span className="text-sm text-gray-500">{quote.quote_number}</span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={14} />
                        <span>{quote.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone size={14} />
                        <span>{quote.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users size={14} />
                        <span>{quote.guest_count} guests</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <DollarSign size={14} />
                        <span className="font-bold text-[#1e3a5f]">
                          ${quote.totals?.total?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Calendar size={12} />
                      <span>Event: {formatDate(quote.event_date)}</span>
                      <span className="mx-2">•</span>
                      <span>Submitted: {formatDate(quote.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedQuote(quote); setShowDetailModal(true); }}
                      className="border-[#1e3a5f] text-[#1e3a5f]"
                    >
                      <Eye size={14} className="mr-1" />
                      View
                    </Button>
                    {quote.status !== 'converted' && (
                      <Select
                        value={quote.status}
                        onValueChange={(value) => updateStatus(quote.id, value)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteQuote(quote.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              Quote Request Details - {selectedQuote?.quote_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div>
                <h4 className="font-semibold text-[#8b7355] mb-2">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium">{selectedQuote.name}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedQuote.email}</span></div>
                  <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{selectedQuote.phone}</span></div>
                  <div><span className="text-gray-500">Venue:</span> <span className="font-medium">{selectedQuote.venue || 'Not specified'}</span></div>
                </div>
              </div>

              {/* Event Details */}
              <div>
                <h4 className="font-semibold text-[#8b7355] mb-2">Event Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">Date:</span> <span className="font-medium">{formatDate(selectedQuote.event_date)}</span></div>
                  <div><span className="text-gray-500">Time:</span> <span className="font-medium">{selectedQuote.event_time || 'Not specified'}</span></div>
                  <div><span className="text-gray-500">Guests:</span> <span className="font-medium">{selectedQuote.guest_count}</span></div>
                  <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{selectedQuote.event_type?.replace('_', ' ')}</span></div>
                </div>
              </div>

              {/* Selections */}
              {selectedQuote.selected_menus?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#8b7355] mb-2">Selected Menus</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedQuote.selected_menus.map((menu, idx) => (
                      <span key={idx} className="px-2 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded text-sm">
                        {menu.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedQuote.selected_bar && (
                <div>
                  <h4 className="font-semibold text-[#8b7355] mb-2">Bar Package</h4>
                  <span className="px-2 py-1 bg-[#c9a227]/10 text-[#8b7355] rounded text-sm">
                    {selectedQuote.selected_bar.name}
                  </span>
                </div>
              )}

              {/* Message */}
              {selectedQuote.message && (
                <div>
                  <h4 className="font-semibold text-[#8b7355] mb-2">Message</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedQuote.message}</p>
                </div>
              )}

              {/* Totals */}
              <div>
                <h4 className="font-semibold text-[#8b7355] mb-2">Quote Total</h4>
                <div className="bg-[#1e3a5f] text-white p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>Estimated Total:</span>
                    <span className="text-2xl font-bold">${selectedQuote.totals?.total?.toFixed(2) || '0.00'}</span>
                  </div>
                  <p className="text-sm text-gray-200 mt-1">
                    ${((selectedQuote.totals?.total || 0) / (selectedQuote.guest_count || 1)).toFixed(2)} per person
                  </p>
                </div>
              </div>

              {/* Actions */}
              {selectedQuote.status !== 'converted' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => convertToContract(selectedQuote.id)}
                    disabled={converting}
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
                  >
                    {converting ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <ArrowRight size={16} className="mr-2" />
                    )}
                    Convert to Contract
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateStatus(selectedQuote.id, 'contacted')}
                    className="border-[#8b7355] text-[#8b7355]"
                  >
                    Mark as Contacted
                  </Button>
                </div>
              )}

              {selectedQuote.status === 'converted' && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
                  <p className="text-green-700 font-medium">This quote has been converted to a contract</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Quote Request Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-quote-request-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1e3a5f] flex items-center gap-2">
              <Plus size={20} />New Quote Request
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Name *</Label>
                <Input
                  value={newQuote.name}
                  onChange={(e) => setNewQuote({ ...newQuote, name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="nq-name"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Email *</Label>
                <Input
                  type="email"
                  value={newQuote.email}
                  onChange={(e) => setNewQuote({ ...newQuote, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="nq-email"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Phone</Label>
                <Input
                  value={newQuote.phone}
                  onChange={(e) => setNewQuote({ ...newQuote, phone: e.target.value })}
                  placeholder="555-555-5555"
                  data-testid="nq-phone"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Event Type</Label>
                <Select
                  value={newQuote.event_type}
                  onValueChange={(v) => setNewQuote({ ...newQuote, event_type: v })}
                >
                  <SelectTrigger data-testid="nq-event-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">Onsite</SelectItem>
                    <SelectItem value="offsite">Offsite</SelectItem>
                    <SelectItem value="cocktail_coach">Cocktail Coach</SelectItem>
                    <SelectItem value="pickup">Catering Pick Up</SelectItem>
                    <SelectItem value="dropoff">Catering Drop Off</SelectItem>
                    <SelectItem value="rustic_ranch">Rustic Ranch</SelectItem>
                    <SelectItem value="celebration_of_life">Celebration of Life</SelectItem>
                    <SelectItem value="bridal_shower">Bridal Shower</SelectItem>
                    <SelectItem value="baby_shower">Baby Shower</SelectItem>
                    <SelectItem value="birthday">Birthday</SelectItem>
                    <SelectItem value="rehearsal_dinner">Rehearsal Dinner</SelectItem>
                    <SelectItem value="golf_tournament">Golf Tournament</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Event Date</Label>
                <Input
                  type="date"
                  value={newQuote.event_date}
                  onChange={(e) => setNewQuote({ ...newQuote, event_date: e.target.value })}
                  data-testid="nq-event-date"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Event Time</Label>
                <Input
                  type="time"
                  value={newQuote.event_time}
                  onChange={(e) => setNewQuote({ ...newQuote, event_time: e.target.value })}
                  data-testid="nq-event-time"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Guest Count</Label>
                <Input
                  type="number"
                  min="1"
                  value={newQuote.guest_count}
                  onChange={(e) => setNewQuote({ ...newQuote, guest_count: e.target.value })}
                  placeholder="50"
                  data-testid="nq-guest-count"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1e3a5f]">Venue / Location</Label>
                <Input
                  value={newQuote.venue}
                  onChange={(e) => setNewQuote({ ...newQuote, venue: e.target.value })}
                  placeholder="Venue or address"
                  data-testid="nq-venue"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#1e3a5f]">Message / Notes</Label>
              <Textarea
                value={newQuote.message}
                onChange={(e) => setNewQuote({ ...newQuote, message: e.target.value })}
                placeholder="Details from the phone call, what they're looking for, special requests…"
                rows={4}
                data-testid="nq-message"
              />
            </div>

            {/* Pricing / Line Items */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-[#1e3a5f]">Quote Line Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLineItem} data-testid="nq-add-line">
                  <Plus size={14} className="mr-1" />Add Line
                </Button>
              </div>
              {(newQuote.line_items || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">Click "Add Line" to start pricing the quote (e.g. "Buffet Dinner × 50 × $45").</p>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-12 gap-1 text-xs text-gray-500 font-semibold">
                    <div className="col-span-6">Description</div>
                    <div className="col-span-2 text-right">Qty</div>
                    <div className="col-span-2 text-right">Unit $</div>
                    <div className="col-span-1 text-right">Total</div>
                    <div className="col-span-1" />
                  </div>
                  {newQuote.line_items.map((li, idx) => {
                    const qty = parseFloat(li.quantity) || 0;
                    const up = parseFloat(li.unit_price) || 0;
                    const lineTotal = qty * up;
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`nq-line-row-${idx}`}>
                        <input
                          type="text" placeholder="Item description"
                          value={li.description || ''}
                          onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                          className="col-span-6 px-2 py-1 text-sm border rounded"
                          data-testid={`nq-line-desc-${idx}`}
                        />
                        <input
                          type="number" min="0" placeholder="0"
                          value={li.quantity ?? ''}
                          onChange={(e) => updateLineItem(idx, { quantity: e.target.value })}
                          className="col-span-2 px-2 py-1 text-sm border rounded text-right"
                          data-testid={`nq-line-qty-${idx}`}
                        />
                        <input
                          type="number" min="0" step="0.01" placeholder="0.00"
                          value={li.unit_price ?? ''}
                          onChange={(e) => updateLineItem(idx, { unit_price: e.target.value })}
                          className="col-span-2 px-2 py-1 text-sm border rounded text-right"
                          data-testid={`nq-line-price-${idx}`}
                        />
                        <div className="col-span-1 text-right text-sm font-semibold text-[#1e3a5f]" data-testid={`nq-line-total-${idx}`}>
                          ${lineTotal.toFixed(2)}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLineItem(idx)}
                          className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"
                          data-testid={`nq-line-remove-${idx}`}
                          title="Remove line"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tax & Gratuity */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <label className="flex items-center gap-2 text-xs">
                  Tax rate
                  <input
                    type="number" min="0" step="0.01"
                    value={(newQuote.tax_rate * 100).toFixed(2)}
                    onChange={(e) => setNewQuote({ ...newQuote, tax_rate: (parseFloat(e.target.value) || 0) / 100 })}
                    className="w-20 px-2 py-1 text-sm border rounded text-right"
                    data-testid="nq-tax-rate"
                  />
                  <span className="text-gray-500">%</span>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={newQuote.include_gratuity}
                    onChange={(e) => setNewQuote({ ...newQuote, include_gratuity: e.target.checked })}
                    data-testid="nq-include-gratuity"
                  />
                  Include gratuity
                  <input
                    type="number" min="0" step="0.01" disabled={!newQuote.include_gratuity}
                    value={(newQuote.gratuity_rate * 100).toFixed(2)}
                    onChange={(e) => setNewQuote({ ...newQuote, gratuity_rate: (parseFloat(e.target.value) || 0) / 100 })}
                    className={`w-20 px-2 py-1 text-sm border rounded text-right ${!newQuote.include_gratuity ? 'bg-gray-100 text-gray-400' : ''}`}
                    data-testid="nq-gratuity-rate"
                  />
                  <span className="text-gray-500">%</span>
                </label>
              </div>

              {/* Computed totals */}
              {(() => {
                const t = computeQuoteTotals(newQuote);
                return (
                  <div className="mt-3 bg-gray-50 border rounded-lg p-3 text-sm space-y-1" data-testid="nq-totals">
                    <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">${t.subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Tax ({(newQuote.tax_rate * 100).toFixed(2)}%)</span><span className="font-medium">${t.taxAmount.toFixed(2)}</span></div>
                    {newQuote.include_gratuity && (
                      <div className="flex justify-between"><span className="text-gray-600">Gratuity ({(newQuote.gratuity_rate * 100).toFixed(2)}%)</span><span className="font-medium">${t.gratuityAmount.toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between border-t pt-1 mt-1">
                      <span className="font-bold text-[#1e3a5f]">Total</span>
                      <span className="font-bold text-[#1e3a5f] text-lg">${t.total.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => { setShowCreateModal(false); setNewQuote(initialNewQuote); }}
                disabled={creating}
                data-testid="nq-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => createQuoteRequest()}
                disabled={creating}
                variant="outline"
                className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
                data-testid="nq-save"
              >
                {creating ? (<><Loader2 size={16} className="animate-spin mr-2" />Saving…</>) : (<><Plus size={16} className="mr-1" />Save Quote Only</>)}
              </Button>
              <Button
                onClick={() => createQuoteRequest({ convertToContract: true })}
                disabled={creating}
                className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
                data-testid="nq-save-and-contract"
              >
                {creating ? (<><Loader2 size={16} className="animate-spin mr-2" />Saving…</>) : (<><ArrowRight size={16} className="mr-1" />Save & Start Contract</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteManagement;
